import { Injectable, signal, computed, effect, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

export type Theme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'klausurenkiste-theme';

@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private platformId = inject(PLATFORM_ID);

    // Signal for reactive theme state
    private readonly _theme = signal<Theme>('dark'); // Default to dark as requested

    // Public readonly signal
    readonly theme = this._theme.asReadonly();

    // Computed signals for convenience
    readonly isDarkMode = computed(() => this._theme() === 'dark');
    readonly isLightMode = computed(() => this._theme() === 'light');

    constructor() {
        if (isPlatformBrowser(this.platformId)) {
            // Initialize theme from storage or system preference
            this.initializeTheme();

            // Apply theme to document whenever it changes
            effect(() => {
                this.applyTheme(this._theme());
            });

            // Listen for system theme changes
            this.listenToSystemThemeChanges();
        }
    }

    /**
     * Initialize theme from localStorage or system preference
     */
    private initializeTheme(): void {
        const storedTheme = localStorage.getItem(THEME_STORAGE_KEY) as Theme | null;

        if (storedTheme && (storedTheme === 'light' || storedTheme === 'dark')) {
            this._theme.set(storedTheme);
        } else {
            // Default to dark mode (as requested by user)
            // Could also check system preference: this.getSystemTheme()
            this._theme.set('dark');
        }
    }

    /**
     * Get system color scheme preference
     */
    private getSystemTheme(): Theme {
        if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
            return 'dark';
        }
        return 'light';
    }

    /**
     * Listen for system theme preference changes
     */
    private listenToSystemThemeChanges(): void {
        if (window.matchMedia) {
            window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
                // Only apply if user hasn't set a preference
                if (!localStorage.getItem(THEME_STORAGE_KEY)) {
                    this._theme.set(e.matches ? 'dark' : 'light');
                }
            });
        }
    }

    /**
     * Apply theme to document element
     */
    private applyTheme(theme: Theme): void {
        document.documentElement.setAttribute('data-theme', theme);

        // Update meta theme-color for mobile browsers
        const metaThemeColor = document.querySelector('meta[name="theme-color"]');
        if (metaThemeColor) {
            metaThemeColor.setAttribute('content', theme === 'dark' ? '#1a1a2e' : '#f8fafc');
        }
    }

    /**
     * Toggle between light and dark mode
     */
    toggleTheme(): void {
        const newTheme: Theme = this._theme() === 'dark' ? 'light' : 'dark';
        this.setTheme(newTheme);
    }

    /**
     * Set specific theme
     */
    setTheme(theme: Theme): void {
        this._theme.set(theme);
        localStorage.setItem(THEME_STORAGE_KEY, theme);
    }

    /**
     * Reset to system preference
     */
    resetToSystemPreference(): void {
        localStorage.removeItem(THEME_STORAGE_KEY);
        this._theme.set(this.getSystemTheme());
    }
}
